import { keepPreviousData, useQuery } from '@tanstack/react-query';
import React, { useEffect, useMemo, useRef } from 'react';

import { Icon } from '@column/column-ui-kit';
import { Badge, DataTable, type DataTableColumnDef } from '@column/ui';

import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { Empty as TableEmpty, getTimestampColumn, tableDefaults, useTimeFormat } from '~/components/Table';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { useNavigate } from '~/lib/navigation';
import { CardAccount, CardAccountRepository } from '~/repositories/CardAccountRepository';
import { CardProgram, CardProgramRepository } from '~/repositories/CardProgramRepository';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { formatString } from '~/util';

const buildColumns = (
  cardProgramsMap: Map<string, CardProgram>,
  timeFormat: TableTimeFormat,
  onTimeFormatChange: (value: TableTimeFormat) => void
): DataTableColumnDef<CardAccount>[] => [
  {
    header: 'Card Account ID',
    accessor: 'id',
    width: 'minmax(200px, 3fr)',
    cell: ({ value }) => <DataTable.TextCell displayVariant="monospaced" weight="medium" value={value ?? ''} />,
  },
  {
    header: 'Card Program',
    accessor: 'cardProgramId',
    width: 'minmax(140px, 2fr)',
    cell: ({ value }) => {
      const program = cardProgramsMap.get(value ?? '');
      return <DataTable.TextCell value={program?.description || value || '-'} />;
    },
  },
  {
    header: 'Account Type',
    id: 'accountType',
    width: 'minmax(min-content, 1fr)',
    cell: ({ row }) => {
      const program = cardProgramsMap.get(row.cardProgramId ?? '');
      return (
        <Badge variant="secondary">{program?.cardProgramType ? formatString(program.cardProgramType) : '-'}</Badge>
      );
    },
  },
  {
    header: 'Status',
    accessor: 'status',
    width: 'minmax(min-content, 1fr)',
    cell: ({ value }) => <Badge variant="secondary">{value ? formatString(value as string) : '-'}</Badge>,
  },
  getTimestampColumn<CardAccount>({
    accessor: 'createdAt',
    timeFormat,
    onTimeFormatChange,
    width: 'minmax(min-content, 1fr)',
    fallback: '-',
  }),
];

export const PageCardAccountsOverview: React.FC = () => {
  const navigate = useNavigate();
  const { currentPlatform, isSandbox } = useSessionStore();
  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    pageSize,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState();
  const { timeFormat, onTimeFormatChange } = useTimeFormat('card-accounts');

  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialRenderRef.current) {
      isInitialRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const {
    data: cardAccountsData,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: ['card-accounts', 'list', currentPlatform?.id, isSandbox, pageSize, cursor],
    queryFn: () => CardAccountRepository.listCardAccounts({ limit: pageSize, startingAfter: cursor }),
    enabled: Boolean(currentPlatform?.id),
    placeholderData: keepPreviousData,
  });

  const { data: cardProgramsData } = useQuery({
    queryKey: ['card-programs', 'list-all', currentPlatform?.id, isSandbox],
    queryFn: () => CardProgramRepository.listCardPrograms(),
    enabled: Boolean(currentPlatform?.id),
  });

  const cardProgramsMap = useMemo(() => {
    const map = new Map<string, CardProgram>();
    (cardProgramsData?.cardPrograms ?? []).forEach((program) => {
      map.set(program.id, program);
    });
    return map;
  }, [cardProgramsData]);

  const cardAccounts = cardAccountsData?.cardAccounts ?? [];
  const hasMore = Boolean(cardAccountsData?.hasMore);

  return (
    <>
      <Loading isLoading={isLoading}>
        <Inner pt={0} px="var(--page-margin)">
          <DataTable<CardAccount>
            {...tableDefaults}
            columns={buildColumns(cardProgramsMap, timeFormat, onTimeFormatChange)}
            data={isError ? [] : cardAccounts}
            rowKey={(row) => row.id}
            isLoading={isFetching}
            emptyContent={
              isError ? (
                <TableEmpty icon={<Icon.Warning />} title="Failed to load card accounts" />
              ) : (
                <TableEmpty
                  icon={<Icon.AnimationWallet />}
                  title="No card accounts found"
                  description="Card accounts will appear here once they're created."
                />
              )
            }
            pagination={{
              hasNext: hasMore,
              hasPrevious,
              pageIndex,
              pageSize,
              onNext: () => {
                if (cardAccounts.length > 0) {
                  onNext({ cursor: cardAccounts[cardAccounts.length - 1].id });
                }
              },
              onPrevious,
              onPageSizeChange: setPageSize,
            }}
            onRowClick={(row) => {
              navigate(`${ROUTE.CARD_ACCOUNTS}/edit/${row.id}`);
            }}
          />
        </Inner>
      </Loading>
    </>
  );
};
